import { scrollToFirstSoulMapProfileError } from '@/pages/soulmap/landing/scroll-to-first-profile-error';
import '@/pages/soulmap/landing/soul-map-landing.css';
import {
  SoulMapProfileFieldErrors,
  SoulMapProfileFields,
} from '@/pages/soulmap/landing/soul-map-profile-fields';
import {
  hydrateBirthPlaceLabel,
  SoulMapProfileForm,
  validateSoulMapProfileForm,
} from '@/utils/soulmap-profile';
import { Icon } from '@iconify/react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@nextui-org/react';
import { useEffect, useState } from 'react';

/**
 * Same form in every case; only the framing copy changes. `partial` exists
 * because "tell us about you" is untrue once some details are already on file.
 */
export type SoulMapProfileModalMode = 'setup' | 'partial' | 'confirm';

const COPY: Record<
  SoulMapProfileModalMode,
  {
    eyebrow: string;
    title: string;
    body: string;
    birthDetailsNote: string;
    action: string;
  }
> = {
  setup: {
    eyebrow: 'Before we begin',
    title: 'Tell us a little about you',
    body: 'These details shape how Soul Map speaks with you, and prepare your chart from the first exchange. You can change them any time.',
    birthDetailsNote: 'Your chart is calculated from these three.',
    action: 'Begin Soul Map',
  },
  partial: {
    eyebrow: 'Almost there',
    title: 'We just need a little more',
    body: "We've kept what we already have. Fill in what's missing, and change anything that does not look right.",
    birthDetailsNote:
      'Your chart is calculated from these three, so we need all of them before we can draw it.',
    action: 'Save and continue',
  },
  confirm: {
    eyebrow: 'Welcome back',
    title: 'Do these still look right?',
    body: "We've kept these from your earlier sessions. Have a look before we begin, and change anything that has drifted.",
    birthDetailsNote:
      'Your chart is calculated from these three. If you correct them, we will draw it again.',
    action: 'Confirm and continue',
  },
};

interface SoulMapProfileConfirmModalProps {
  isOpen: boolean;
  mode: SoulMapProfileModalMode;
  initialForm: SoulMapProfileForm;
  /** Stored birth_place text, used to recover a place that has no coordinates. */
  storedBirthPlaceLabel: string;
  isLoading: boolean;
  isSubmitting: boolean;
  /**
   * Field errors from a rejected save. Shown under their fields, beside the
   * form's own validation, until the user edits that field.
   */
  serverErrors?: SoulMapProfileFieldErrors;
  /**
   * `presentedForm` is what the user was actually shown, which differs from
   * `initialForm` once a birth place is recovered from the catalog. Callers
   * compare against it so recovering coordinates is not read as an edit.
   */
  onSubmit: (
    form: SoulMapProfileForm,
    presentedForm: SoulMapProfileForm
  ) => void;
  /**
   * Offered whenever details are still incomplete. Completing the form is the
   * sole way into Soul Map, so a user who cannot or will not answer needs a
   * named way out. Leaving writes nothing, keeping the modal armed for next
   * time.
   */
  onExit: () => void;
}

export function SoulMapProfileConfirmModal({
  isOpen,
  mode,
  initialForm,
  storedBirthPlaceLabel,
  isLoading,
  isSubmitting,
  serverErrors,
  onSubmit,
  onExit,
}: SoulMapProfileConfirmModalProps) {
  const [form, setForm] = useState<SoulMapProfileForm>(initialForm);
  const [presentedForm, setPresentedForm] =
    useState<SoulMapProfileForm>(initialForm);
  const [errors, setErrors] = useState<SoulMapProfileFieldErrors>({});
  const [isResolvingBirthPlace, setIsResolvingBirthPlace] = useState(false);
  /**
   * In confirm mode every field is filled, so validation never stops a user
   * who has not scrolled down to the birth details. Hold the button until the
   * last field, City of birth, has been on screen at least once.
   */
  const [hasReviewedFields, setHasReviewedFields] = useState(false);
  const [fieldsContainer, setFieldsContainer] = useState<HTMLDivElement | null>(
    null
  );
  const needsReview = mode === 'confirm' && !hasReviewedFields;

  useEffect(() => {
    setForm(initialForm);
    setPresentedForm(initialForm);
  }, [initialForm]);

  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setHasReviewedFields(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (serverErrors && Object.keys(serverErrors).length > 0) {
      setErrors((current) => ({ ...current, ...serverErrors }));
      scrollToFirstSoulMapProfileError(serverErrors);
    }
  }, [serverErrors]);

  useEffect(() => {
    if (!isOpen || initialForm.birthPlace || !storedBirthPlaceLabel) {
      setIsResolvingBirthPlace(false);
      return;
    }

    let cancelled = false;
    setIsResolvingBirthPlace(true);

    void hydrateBirthPlaceLabel(storedBirthPlaceLabel)
      .then((birthPlace) => {
        if (cancelled || !birthPlace) return;
        setForm((current) =>
          current.birthPlace ? current : { ...current, birthPlace }
        );
        setPresentedForm((current) =>
          current.birthPlace ? current : { ...current, birthPlace }
        );
      })
      .catch(() => {
        // Leave the stored label visible; the user can still pick a city.
      })
      .finally(() => {
        if (!cancelled) setIsResolvingBirthPlace(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, initialForm.birthPlace, storedBirthPlaceLabel]);

  useEffect(() => {
    const lastField = fieldsContainer?.querySelector('#soulmap-birth-place');
    if (!needsReview || !lastField) return;

    // Root defaults to the viewport, but intersection still respects the
    // modal body's scroll clipping. Fires at once when the form already fits.
    // Most of the input, not a sliver of its border, has to be in view.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasReviewedFields(true);
        }
      },
      { threshold: 0.75 }
    );
    observer.observe(lastField);
    return () => observer.disconnect();
  }, [needsReview, fieldsContainer]);

  const copy = COPY[mode];
  const isBusy = isLoading || isSubmitting;

  const updateForm = (patch: Partial<SoulMapProfileForm>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const clearError = (field: keyof SoulMapProfileFieldErrors) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = () => {
    const validationErrors = validateSoulMapProfileForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstSoulMapProfileError(validationErrors);
      return;
    }
    onSubmit(form, presentedForm);
  };

  return (
    <Modal
      isOpen={isOpen}
      isDismissable={false}
      isKeyboardDismissDisabled
      hideCloseButton
      placement="center"
      scrollBehavior="inside"
      size="md"
      // NextUI's default z-50 sits under the mobile input bar (z-[999]) and
      // bottom nav (z-[100]); match the app's overlay layer instead.
      classNames={{ backdrop: 'z-[9999]', wrapper: 'z-[10000]' }}
    >
      <ModalContent className="sm-scope">
        <ModalHeader className="flex flex-col gap-2 pb-2">
          <span className="sm-eyebrow">{copy.eyebrow}</span>
          <h2
            className="sm-serif"
            style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.25 }}
          >
            {copy.title}
          </h2>
          <p
            className="font-normal"
            style={{
              fontSize: 14.5,
              color: 'var(--sm-muted)',
              lineHeight: 1.6,
            }}
          >
            {copy.body}
          </p>
        </ModalHeader>
        <ModalBody>
          {/* Never swap back to the spinner mid-save: the form would vanish
              behind the modal's closing animation. */}
          {isLoading && !isSubmitting ? (
            <div className="flex justify-center py-10">
              <Spinner size="lg" color="primary" />
            </div>
          ) : (
            <div className="space-y-6" ref={setFieldsContainer}>
              <SoulMapProfileFields
                form={form}
                errors={errors}
                disabled={isBusy}
                onChange={updateForm}
                onClearError={clearError}
                showSectionHeadings
                birthDetailsNote={copy.birthDetailsNote}
                isResolvingBirthPlace={isResolvingBirthPlace}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter
          className={`${mode === 'setup' || needsReview ? 'justify-between' : 'justify-end'} border-t mt-4 text-right`}
        >
          {needsReview && !isLoading && (
            <p
              id="soulmap-review-hint"
              className="flex min-w-0 items-center gap-1.5 text-left"
              style={{
                fontSize: 12,
                lineHeight: 1.35,
                color: 'var(--sm-muted)',
              }}
            >
              <Icon icon="lucide:info" className="shrink-0" width={14} />
              Scroll down to review your details
            </p>
          )}
          {mode === 'setup' && (
            <Button
              type="button"
              variant="bordered"
              onClick={onExit}
              disabled={isBusy}
            >
              Back to LiFT Hub
            </Button>
          )}
          <Button
            type="button"
            color="primary"
            // The review hint shares this row; let it wrap, not squeeze the label.
            className="shrink-0"
            onClick={handleSubmit}
            disabled={isBusy || isResolvingBirthPlace || needsReview}
            aria-describedby={needsReview ? 'soulmap-review-hint' : undefined}
          >
            {isSubmitting ? 'Saving…' : copy.action}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
